/**
 * CDSCardStrip — Horizontal CDS Card Strip (Cumulus Spec 7A)
 *
 * Compact horizontal strip for the center column top-of-chart.
 * Max 3 cards visible, 72px height, severity-coded left borders,
 * "Show N more" overflow. Each card terminates in an existing
 * modal flow (prescribe / lab order / schedule).
 *
 * Uses the same data-fetching and card-building logic as CDSTriagePanel
 * but renders in a constrained horizontal layout optimized for scanning.
 *
 * Severity colors per spec:
 *   ACTION → red left border (urgent, needs clinician action)
 *   WATCH  → amber left border (monitor, approaching threshold)
 *   INFO   → blue left border (informational, no action required)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  Loader2,
  Pill,
  ShieldAlert,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  aiRecommendationService,
  parseRecommendation,
} from '@/services/aiRecommendationService';
import { prescriptionService } from '@/services/prescriptionService';
import { cn } from '@/lib/utils';

// ── Types ──

type Severity = 'action' | 'watch' | 'info';
type ActionType = 'prescribe' | 'order_lab' | 'schedule' | 'view_labs' | 'dismiss';

interface CDSCard {
  id: string;
  severity: Severity;
  headline: string;
  evidence?: string;
  actions: { type: ActionType; label: string }[];
}

interface CDSCardStripProps {
  patientId: string | null;
  patientName?: string | null;
  medicationAllergies?: string[] | null;
  onPrescribe?: () => void;
  onOrderLab?: () => void;
  onSchedule?: () => void;
  className?: string;
}

// ── Severity Styles (spec 7A: red/amber/blue left borders) ──

const SEVERITY_BORDER: Record<Severity, string> = {
  action: 'border-l-red-500',
  watch: 'border-l-amber-500',
  info: 'border-l-blue-500',
};

const SEVERITY_BG: Record<Severity, string> = {
  action: 'bg-red-50/60',
  watch: 'bg-amber-50/60',
  info: 'bg-blue-50/40',
};

const SEVERITY_PILL: Record<Severity, { bg: string; text: string }> = {
  action: { bg: 'bg-red-100', text: 'text-red-800' },
  watch: { bg: 'bg-amber-100', text: 'text-amber-800' },
  info: { bg: 'bg-blue-100', text: 'text-blue-700' },
};

const SEVERITY_ICON: Record<Severity, React.ElementType> = {
  action: AlertTriangle,
  watch: AlertTriangle,
  info: CheckCircle2,
};

const ACTION_ICON: Record<ActionType, React.ElementType> = {
  prescribe: Pill,
  order_lab: FlaskConical,
  schedule: Calendar,
  view_labs: FlaskConical,
  dismiss: X,
};

// ── Parsing helpers (shared with CDSTriagePanel) ──

interface ParsedRecs {
  meds: string[];
  labs: string[];
  followUp: string | null;
}

function parseReasoning(raw: string | object | null): ParsedRecs {
  if (!raw) return { meds: [], labs: [], followUp: null };
  const text = typeof raw === 'string' ? raw : JSON.stringify(raw);

  const meds: string[] = [];
  const medRegex = /\*\*([^*]+)\*\*[^-\n]*[-–—]\s*(.+)/g;
  let m: RegExpExecArray | null;
  while ((m = medRegex.exec(text)) !== null) {
    meds.push(`${m[1].trim()} — ${m[2].trim()}`.slice(0, 100));
  }

  const labs: string[] = [];
  const labSection = text.match(
    /(?:Recommended Laboratory|Labs?\s*(?:Needed|Recommended))([\s\S]*?)(?:\n###|\n##|$)/i,
  );
  if (labSection) {
    labSection[1]
      .split('\n')
      .filter((l) => l.trim().startsWith('-') || l.trim().startsWith('•'))
      .forEach((l) => {
        const cleaned = l.replace(/^[-•*]\s*\*?\*?/, '').replace(/\*\*/g, '').trim();
        if (cleaned) labs.push(cleaned);
      });
  }

  let followUp: string | null = null;
  const fu = text.match(/(?:Follow[- ]?up|Schedule)([\s\S]*?)(?:\n###|\n##|$)/i);
  if (fu) {
    const first = fu[1].trim().split('\n')[0].replace(/^[-•*]\s*/, '').trim();
    if (first) followUp = first;
  }

  return { meds, labs, followUp };
}

// ── Component ──

const MAX_VISIBLE = 3;

const CDSCardStrip: React.FC<CDSCardStripProps> = ({
  patientId,
  medicationAllergies,
  onPrescribe,
  onOrderLab,
  onSchedule,
  className,
}) => {
  const [reasoning, setReasoning] = useState<string | object | null>(null);
  const [activeRxCount, setActiveRxCount] = useState<number | null>(null);
  const [nearestRefillDays, setNearestRefillDays] = useState<number | null>(null);
  const [recStatus, setRecStatus] = useState<'ready' | 'processing' | 'pending_data' | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setIsLoading(true);
    setError(null);
    let recOk = false;
    let rxOk = false;
    try {
      const [recRes, rxRes] = await Promise.allSettled([
        aiRecommendationService.getPatientRecommendations(patientId, 3),
        prescriptionService.getPrescriptionsByPatientId(patientId, { page: 1, limit: 25 }),
      ]);

      // AI recommendations — fail gracefully (patient may not be synced yet)
      if (recRes.status === 'fulfilled' && recRes.value.success) {
        setReasoning(parseRecommendation(recRes.value.data?.recommendation ?? null));
        setRecStatus(recRes.value.data?.meta?.status ?? 'ready');
        recOk = true;
      } else if (recRes.status === 'fulfilled') {
        setRecStatus(recRes.value.data?.meta?.status ?? 'pending_data');
        // Not an error — AI data just isn't ready yet
      }
      // If recRes rejected entirely, we still continue with Rx data

      // Prescription data — independent of AI recommendations
      if (rxRes.status === 'fulfilled') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload: any = rxRes.value;
        // API envelope is { success, data: { prescriptions: [...], pagination } }.
        // Probe each plausible shape and always coerce to an array.
        const candidates = [
          payload?.data?.data?.prescriptions,
          payload?.data?.prescriptions,
          payload?.data?.data,
          payload?.data,
          payload,
        ];
        const raw = candidates.find((c: unknown) => Array.isArray(c)) ?? [];
        const list: Array<{ status?: string; created_at?: string; followup_days?: number | null }>
          = raw as Array<{ status?: string; created_at?: string; followup_days?: number | null }>;
        const active = list.filter((p) => (p?.status ?? '').toLowerCase() === 'active');
        setActiveRxCount(active.length);
        rxOk = true;

        const now = Date.now();
        const days = active
          .map((p) => {
            if (!p.created_at) return null;
            const created = new Date(p.created_at).getTime();
            const window = (p.followup_days ?? 30) * 86_400_000;
            return Math.round((created + window - now) / 86_400_000);
          })
          .filter((n): n is number => n !== null);
        setNearestRefillDays(days.length > 0 ? Math.min(...days) : null);
      }

      // Only show error if BOTH sources failed completely
      if (!recOk && !rxOk) {
        setError('Unable to load clinical data');
      }
    } catch {
      setError('Unable to load clinical data');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => { void load(); }, [load]);

  // ── Build cards (same logic as CDSTriagePanel) ──

  const cards = useMemo<CDSCard[]>(() => {
    const out: CDSCard[] = [];
    const parsed = parseReasoning(reasoning);

    if (parsed.meds.length > 0) {
      out.push({
        id: 'rx-rec',
        severity: 'action',
        headline: parsed.meds.length === 1
          ? 'Medication recommendation'
          : `${parsed.meds.length} medication recommendations`,
        evidence: parsed.meds.slice(0, 2).join(' · '),
        actions: [
          { type: 'prescribe', label: 'Prescribe' },
          { type: 'dismiss', label: 'Defer' },
        ],
      });
    }

    if (parsed.labs.length > 0) {
      out.push({
        id: 'lab-rec',
        severity: 'watch',
        headline: `${parsed.labs.length} lab${parsed.labs.length > 1 ? 's' : ''} recommended`,
        evidence: parsed.labs.slice(0, 2).join(', '),
        actions: [
          { type: 'order_lab', label: 'Order' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    if (nearestRefillDays !== null && nearestRefillDays <= 14 && nearestRefillDays >= 0) {
      out.push({
        id: 'refill-window',
        severity: nearestRefillDays <= 7 ? 'watch' : 'info',
        headline: nearestRefillDays === 0
          ? 'Refill due today'
          : `Refill in ${nearestRefillDays}d`,
        evidence: 'Approaching refill threshold',
        actions: [
          { type: 'prescribe', label: 'Renew' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    if (parsed.followUp) {
      out.push({
        id: 'followup',
        severity: 'info',
        headline: 'Follow-up recommended',
        evidence: parsed.followUp.slice(0, 80),
        actions: [
          { type: 'schedule', label: 'Schedule' },
          { type: 'dismiss', label: 'Dismiss' },
        ],
      });
    }

    const allergies = (medicationAllergies ?? []).filter(Boolean);
    if (allergies.length > 0) {
      out.push({
        id: 'allergy-guard',
        severity: 'info',
        headline: 'Allergy guard active',
        evidence: allergies.slice(0, 3).join(', '),
        actions: [{ type: 'dismiss', label: 'Ack' }],
      });
    }

    if (out.length === 0 && activeRxCount !== null) {
      out.push({
        id: 'drug-count',
        severity: 'info',
        headline: activeRxCount === 0 ? 'No active meds' : `${activeRxCount} active med${activeRxCount === 1 ? '' : 's'}`,
        evidence: activeRxCount === 0 ? 'No prescriptions on file' : 'Review before prescribing',
        actions: activeRxCount === 0
          ? [{ type: 'prescribe', label: 'Start Rx' }]
          : [{ type: 'dismiss', label: 'Ack' }],
      });
    }

    return out.filter((c) => !dismissed.has(c.id));
  }, [reasoning, nearestRefillDays, activeRxCount, medicationAllergies, dismissed]);

  const handleAction = (card: CDSCard, type: ActionType) => {
    if (type === 'dismiss') {
      setDismissed((prev) => { const next = new Set(prev); next.add(card.id); return next; });
      return;
    }
    if (type === 'prescribe') onPrescribe?.();
    else if (type === 'order_lab') onOrderLab?.();
    else if (type === 'schedule') onSchedule?.();
  };

  // ── Empty / loading / error states ──

  if (!patientId) return null;

  if (isLoading) {
    return (
      <div className={cn('rounded-lg border border-slate-200 bg-white px-4 h-[72px] flex items-center gap-2', className)}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
        <span className="text-xs text-slate-500">Analyzing patient data…</span>
      </div>
    );
  }



  if (cards.length === 0) {
    // Graceful suppress: no error banners. If data isn't ready or both
    // sources failed, render nothing. Partial Rx-derived cards (drug count,
    // refill window, allergy guard) render above when available.
    if (error) return null;
    if (recStatus === 'pending_data') return null;
    return null;
  }

  // ── Visible cards + overflow ──

  const visibleCards = showAll ? cards : cards.slice(0, MAX_VISIBLE);
  const overflowCount = cards.length - MAX_VISIBLE;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Clinical Decision Support
        </span>
        <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Live · {cards.length} alert{cards.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Horizontal card strip */}
      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {visibleCards.map((card) => {
          const Icon = SEVERITY_ICON[card.severity];
          const pill = SEVERITY_PILL[card.severity];
          const primaryAction = card.actions.find((a) => a.type !== 'dismiss');
          const PrimaryIcon = primaryAction ? ACTION_ICON[primaryAction.type] : null;

          return (
            <div
              key={card.id}
              className={cn(
                'relative rounded-lg border border-l-[3px] h-[72px] px-3 py-2 flex flex-col justify-between',
                'border-slate-200',
                SEVERITY_BORDER[card.severity],
                SEVERITY_BG[card.severity],
              )}
            >
              {/* Top: severity pill + headline */}
              <div className="flex items-center gap-1.5 min-w-0">
                <Icon className={cn('h-3 w-3 flex-shrink-0', card.severity === 'action' ? 'text-red-500' : card.severity === 'watch' ? 'text-amber-500' : 'text-blue-500')} />
                <span className={cn('rounded px-1 py-0.5 text-[9px] font-bold tracking-wider flex-shrink-0', pill.bg, pill.text)}>
                  {card.severity.toUpperCase()}
                </span>
                <span className="text-xs font-medium text-slate-900 truncate">
                  {card.headline}
                </span>
              </div>

              {/* Bottom: evidence + action button */}
              <div className="flex items-center justify-between gap-2 min-w-0">
                <span className="text-[11px] text-slate-500 truncate flex-1">
                  {card.evidence}
                </span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {primaryAction && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'h-6 px-2 text-[10px] font-medium',
                        card.severity === 'action'
                          ? 'border-red-300 text-red-700 hover:bg-red-50'
                          : card.severity === 'watch'
                            ? 'border-amber-300 text-amber-700 hover:bg-amber-50'
                            : 'border-blue-300 text-blue-600 hover:bg-blue-50',
                      )}
                      onClick={() => handleAction(card, primaryAction.type)}
                    >
                      {PrimaryIcon && <PrimaryIcon className="h-3 w-3 mr-0.5" />}
                      {primaryAction.label}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-slate-300 hover:text-slate-500"
                    onClick={() => handleAction(card, 'dismiss')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Overflow toggle */}
      {overflowCount > 0 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700 transition-colors mx-auto"
        >
          {showAll ? (
            <>
              <ChevronUp className="h-3 w-3" />
              Show fewer
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              Show {overflowCount} more alert{overflowCount !== 1 ? 's' : ''}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CDSCardStrip;
